const moment = require('moment');

/**
 * Calculates billable hours worked based on clock-in/out times,
 * applying 5-minute rounding rules for payroll and billing.
 *
 * RULE SUMMARY:
 * ----------------------------------------------------------
 * Key Definitions:
 *  - 5-minute mark: Every :00, :05, :10, :15, :20, :25, :30, :35, :40, :45, :50, :55 within the hour.
 *  - ceil5(t): Round UP to the next 5-minute mark.
 *  - floor5(t): Round DOWN to the previous 5-minute mark.
 *  - S_start / S_end: Scheduled start and end times.
 *
 * Core Rules:
 * ----------------------------------------------------------
 * 1. Clock-ins → Always round UP.
 *      rounded_in = ceil5(actual_in)
 *      Billing starts from rounded_in.
 *      May bill before S_start if rounded_in < S_start.
 *
 * 2. Clock-outs → Conditional rounding.
 *      a) If actual_out ≤ S_end:
 *            rounded_out = min(ceil5(actual_out), S_end)
 *         (Round UP but never beyond the scheduled end.)
 *
 *      b) If actual_out > S_end:
 *            rounded_out = floor5(actual_out)
 *         (Round DOWN after S_end to avoid overstating overtime.)
 *
 * 3. Billable duration:
 *      billable_minutes = max(0, rounded_out − rounded_in)
 *      hoursWorked = billable_minutes / 60
 *
 *
 * @param {Object} params - Input parameters
 * @param {string} params.scheduleStartIso - Scheduled shift start (ISO 8601)
 * @param {string} params.scheduleEndIso - Scheduled shift end (ISO 8601)
 * @param {string} params.actualInIso - Actual clock-in time (ISO 8601)
 * @param {string} params.actualOutIso - Actual clock-out time (ISO 8601)
 * @return {Object} Returns calculated work details.
 * @return {string} return.roundedIn - Rounded clock-in time (ISO 8601)
 * @return {string} return.roundedOut - Rounded clock-out time (ISO 8601)
 * @return {number} return.hoursWorked - Total billable hours (two decimals)
 */
const calculateWorkedHours = ({scheduleStartIso, scheduleEndIso, actualInIso, actualOutIso}) => {
  const INTERVAL = 5; // minutes per rounding interval

  const actualIn = moment(actualInIso);
  const actualOut = moment(actualOutIso);
  const sEnd = moment(scheduleEndIso);

  // --- Helper rounding functions ---
  const ceil5 = (m) => {
    const minutes = m.minutes();
    const mod = minutes % INTERVAL;
    if (mod === 0) return m.clone().seconds(0).milliseconds(0);
    return m.clone().add(INTERVAL - mod, 'minutes').seconds(0).milliseconds(0);
  };

  const floor5 = (m) => {
    const minutes = m.minutes();
    const mod = minutes % INTERVAL;
    return m.clone().subtract(mod, 'minutes').seconds(0).milliseconds(0);
  };

  // --- Rule 1: Clock-ins → Always round UP ---
  const roundedIn = ceil5(actualIn);

  // --- Rule 2: Clock-outs → Conditional rounding ---
  let roundedOut;
  if (actualOut.isSameOrBefore(sEnd)) {
    // 2a. Before or at scheduled end → round up
    roundedOut = ceil5(actualOut);
  } else {
    // 2b. After scheduled end → round down
    roundedOut = floor5(actualOut);
  }
  // 2c. If clock-out is before clock-in, then set the clockout to be the clock-in
  if (roundedOut.isBefore(roundedIn)) roundedOut = roundedIn.clone();

  // --- Rule 3: Billable duration (no negatives) ---
  const diffMinutes = Math.max(0, roundedOut.diff(roundedIn, 'minutes'));
  const hoursWorked = Number((diffMinutes / 60).toFixed(2));

  return {
    roundedIn: roundedIn.toISOString(),
    roundedOut: roundedOut.toISOString(),
    hoursWorked,
  };
};

module.exports = {
  calculateWorkedHours,
};
